// app.json `public`: what a published App shows anonymous visitors and what
// they may send (apps/public). Declaring is not publishing: the App's owner
// publishes a saved version, and only then are the declared rows copied.
//
// - `collections`: each names one of the App's collections. Visitors read a
//   copy taken at publish (refreshable), never the live table: only `fields`
//   (every field when absent; on a records table, keys of each record) of the
//   rows `filter` matches (the typed query filter; every row when absent), at
//   most APP_PUBLIC.snapshotRows.
// - `actions`: each is one declared write a visitor may make. `append` adds
//   one row to `collectionId` with the visitor's values for `fields` only;
//   `stamp` names a field the server fills with the time received.
import { z } from 'zod';
import { appId, appText, uniqueBy } from './app-v2-primitives.mjs';
import { APP_PUBLIC } from './app-limits.mjs';

/** Field types a visitor may fill. Files, rich text, relations and JSON are
 * never written by a public action (a records table's payload keys are). */
export const PUBLIC_ACTION_FIELD_TYPES = Object.freeze([
  'text',
  'integer',
  'decimal',
  'boolean',
  'date',
  'timestamp',
  'enum',
]);

const unique = (items) => new Set(items).size === items.length;
const exposed = z
  .object({
    id: appId,
    fields: z.array(appId).min(1).max(200).refine(unique).optional(),
    filter: z.json().optional(),
  })
  .strict();
const action = z
  .object({
    id: appId,
    label: appText(200).optional(),
    kind: z.literal('append'),
    collectionId: appId,
    fields: z.array(appId).min(1).max(APP_PUBLIC.actionFields).refine(unique),
    stamp: appId.optional(),
  })
  .strict();

const entries = (schema, max) =>
  z.array(schema).max(max).refine(uniqueBy('id'), 'Declaration IDs must be unique').default([]);

export const publicDeclarationSchema = z
  .object({
    collections: entries(exposed, APP_PUBLIC.collections),
    actions: entries(action, APP_PUBLIC.actions),
  })
  .strict();

/** A records table (`app.records(id)`): one JSON field named `payload`. */
export const isRecordsCollection = (collection) =>
  collection.fields.length === 1 &&
  collection.fields[0].id === 'payload' &&
  collection.fields[0].type === 'json';

function fieldProblems(entry, byId) {
  return entry.fields.flatMap((id, index) => {
    const field = byId.get(id);
    if (!field) return [[`fields.${index}`, 'Reference a field of the collection']];
    return PUBLIC_ACTION_FIELD_TYPES.includes(field.type)
      ? []
      : [[`fields.${index}`, `A visitor cannot fill a ${field.type} field`]];
  });
}

function stampProblems(entry, byId) {
  if (entry.stamp === undefined) return [];
  const stamp = byId.get(entry.stamp);
  const fits = stamp && ['timestamp', 'text'].includes(stamp.type);
  return fits && !entry.fields.includes(entry.stamp)
    ? []
    : [['stamp', 'Name a timestamp or text field the visitor does not fill']];
}

function requiredProblems(entry, collection) {
  const filled = new Set([...entry.fields, entry.stamp]);
  return collection.fields
    .filter((field) => field.required && !filled.has(field.id) && field.default === undefined)
    .map((field) => ['fields', `Fill the required field ${field.id}, or give it a default`]);
}

function actionProblems(entry, collection) {
  if (!collection) return [['collectionId', 'Reference a declared collection']];
  if (isRecordsCollection(collection))
    return entry.fields.includes('id') ? [['fields', 'A record field may not be named id']] : [];
  const byId = new Map(collection.fields.map((field) => [field.id, field]));
  return [
    ...fieldProblems(entry, byId),
    ...stampProblems(entry, byId),
    ...requiredProblems(entry, collection),
  ];
}

// A filter term that fixes a field to one value: `eq` with a literal, alone or
// inside a top-level `and`. Every copied row holds exactly that value.
function fixedFields(filter) {
  const terms = filter?.kind === 'and' && Array.isArray(filter.terms) ? filter.terms : [filter];
  return terms
    .filter((term) => term?.kind === 'compare' && term.operator === 'eq' && term.value !== null)
    .map((term) =>
      term.columnId === 'payload' && term.path?.length === 1
        ? term.path[0]
        : term.path
          ? null
          : term.columnId,
    )
    .filter((id) => typeof id === 'string');
}

/**
 * The fields a public copy holds: the declared ones (every field of a typed
 * table when absent) and those its filter fixes to one value, which the App's
 * own code may still test (`published === true`) and which reveal nothing the
 * filter does not. On a records table these are record keys.
 */
export function publicFields(entry, collection) {
  const declared =
    entry.fields ??
    (isRecordsCollection(collection) ? [] : collection.fields.map((field) => field.id));
  return [...new Set([...declared, ...fixedFields(entry.filter)])];
}

/** The columns a typed query filter compares (shape is checked when it runs). */
function filterColumns(node, found = []) {
  if (!node || typeof node !== 'object') return found;
  if (Array.isArray(node.terms)) for (const term of node.terms) filterColumns(term, found);
  else if (typeof node.columnId === 'string') found.push(node.columnId);
  return found;
}

/** References from `public` to the App's own collections and fields. */
export function validatePublicReferences(manifest, ctx) {
  if (!manifest.public) return;
  const collections = new Map(manifest.collections.map((item) => [item.id, item]));
  const issue = (path, message) =>
    ctx.addIssue({ code: 'custom', path: ['public', ...path], message });
  manifest.public.collections.forEach((entry, index) => {
    const collection = collections.get(entry.id);
    if (!collection) return issue(['collections', index, 'id'], 'Reference a declared collection');
    // On a records table (`app.records`), `fields` and filter columns are keys
    // of each record, and `fields` is required: the owner sees every key that
    // becomes public, never a whole record behind the one field `payload`.
    if (isRecordsCollection(collection)) {
      if (!entry.fields)
        issue(['collections', index, 'fields'], 'List the record keys visitors may see');
      return;
    }
    const fields = new Set(collection.fields.map((field) => field.id));
    entry.fields?.forEach((id, at) => {
      if (!fields.has(id))
        issue(['collections', index, 'fields', at], 'Reference a field of the collection');
    });
    for (const columnId of filterColumns(entry.filter))
      if (!fields.has(columnId))
        issue(
          ['collections', index, 'filter'],
          `Filter on a field of the collection, not ${columnId}`,
        );
  });
  manifest.public.actions.forEach((entry, index) => {
    for (const [path, message] of actionProblems(entry, collections.get(entry.collectionId)))
      issue(['actions', index, ...path.split('.')], message);
  });
}
